import type { Meeting } from './types'

const LIVE_STATUS = new Set(['ACTIVE', 'PAUSED'])
const OK_COMPANION = new Set(['waiting_to_join', 'in_meeting'])

/**
 * Parse an API datetime to epoch ms. The core API returns either an ISO string
 * with a zone ("2026-06-29T14:30:00Z" / "…+09:00") or a bare space-separated
 * UTC string ("2026-06-29 14:30:00"). `Date.parse` reads the bare form as LOCAL
 * time, which makes liveness timezone-dependent. Normalize the bare form to ISO
 * UTC (swap the space for "T", append "Z") so the comparison is always against
 * real UTC. Returns NaN for anything unparseable.
 */
export function parseApiTime(value: string | null | undefined): number {
  if (!value) return Number.NaN
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value)
  const iso = hasZone ? value : `${value.replace(' ', 'T')}Z`
  return Date.parse(iso)
}

/** A finished meeting lingers this long (meetings run over; hard cuts jar). */
const OVERRUN_GRACE_MS = 15 * 60_000
/** A no-end-date session can't stay "live" forever — cap at a work-morning. */
const NO_END_MAX_MS = 4 * 3600_000

/**
 * Live = the session is Active/Paused, an actual meeting (not a collab), its
 * bot companion hasn't finished, it has started, and it hasn't ended.
 *
 * Two deliberate departures from the dashboard's LiveMeetingsContext rule:
 * - NO invite_pulse requirement. Pulse-calendar meetings (what Nova's combined
 *   calendar shows) arrive with invite_pulse=false but are absolutely real —
 *   requiring it made every pulse-calendar meeting invisible here.
 * - An END-time guard instead. The API keeps never-closed sessions ACTIVE for
 *   days (observed live); without invite_pulse filtering them by accident, the
 *   end time is the honest liveness signal: grace past end_at, and a hard cap
 *   for sessions with no end_at at all.
 */
export function isMeetingLive(m: Meeting, nowMs: number): boolean {
  if (!m.status || !LIVE_STATUS.has(m.status)) return false
  if (m.type && m.type !== 'MEETING') return false
  if (m.companionStatus && !OK_COMPANION.has(m.companionStatus)) return false
  const s = parseApiTime(m.startAt)
  if (!Number.isNaN(s) && s > nowMs) return false
  const e = parseApiTime(m.endAt)
  if (!Number.isNaN(e)) {
    if (nowMs > e + OVERRUN_GRACE_MS) return false
    // Laravel serializes a NULL end_at through Carbon::parse(null) == now(),
    // so a never-closed session arrives with end_at ~= the fetch moment and
    // the no-end cap below never runs. If the session is older than the cap
    // AND its "end" hugs the current clock, it's that ghost — not live.
    if (
      !Number.isNaN(s) &&
      nowMs - s > NO_END_MAX_MS &&
      Math.abs(e - nowMs) < 5 * 60_000
    ) {
      return false
    }
  } else if (!Number.isNaN(s) && nowMs - s > NO_END_MAX_MS) {
    return false
  }
  return true
}

/** Live meetings, earliest-started first. */
export function liveMeetings(meetings: Meeting[], nowMs: number): Meeting[] {
  return meetings
    .filter((m) => isMeetingLive(m, nowMs))
    .sort((a, b) => (a.startAt ?? '').localeCompare(b.startAt ?? ''))
}

/** Every userId currently in a live meeting (deduped) — invited ∪ joined,
 *  matching the set the world actually seats at tables. */
export function inMeetingUserIds(meetings: Meeting[], nowMs: number): Set<string> {
  const ids = new Set<string>()
  for (const m of liveMeetings(meetings, nowMs)) {
    for (const u of m.invitedUserIds) ids.add(u)
  }
  return ids
}

/**
 * Today's still-upcoming MEETING sessions, earliest-first. A session counts as
 * "next" when it's a real meeting (not a collab) whose start is in the future —
 * the not-yet-live siblings already present in WorldSnapshot.meetings. Idle
 * meeting-room boards display these; we don't pin them to a specific room.
 */
export function upcomingMeetings(meetings: Meeting[], nowMs: number): Meeting[] {
  return meetings
    .filter((m) => {
      if (m.type && m.type !== 'MEETING') return false
      if (!m.startAt) return false
      const s = parseApiTime(m.startAt)
      return !Number.isNaN(s) && s > nowMs
    })
    .sort((a, b) => parseApiTime(a.startAt) - parseApiTime(b.startAt))
}

/** Today's upcoming MEETING sessions for a specific pulse, earliest-first. */
export function upcomingForPulse(
  meetings: Meeting[],
  pulseId: string | null,
  nowMs: number,
): Meeting[] {
  if (!pulseId) return []
  return upcomingMeetings(meetings, nowMs).filter((m) => m.pulseId === pulseId)
}
